import type { ReactNode } from 'react';
import { cssVars } from './cssVars';
import { Icon } from './Icon';

/** `span.badge` coloured by lifecycle (`data-life`) or status (`data-status`). */
export function Badge({
  life,
  status,
  children,
}: {
  life?: string;
  status?: string;
  children: ReactNode;
}) {
  return (
    <span className="badge" data-life={life} data-status={status}>
      {children}
    </span>
  );
}

interface SegmentedProps<T extends string> {
  label: string;
  options: readonly { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}

/** `.segmented` toggle group (`aria-pressed`). */
export function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
}: SegmentedProps<T>) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          className="segmented__opt"
          type="button"
          aria-pressed={option.value === value}
          onClick={() => {
            onChange(option.value);
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

interface TabsProps<T extends string> {
  label: string;
  tabs: readonly { id: T; label: string; count?: number | string }[];
  value: T;
  onChange: (value: T) => void;
}

/** `.tabs[role=tablist]` with counts. */
export function Tabs<T extends string>({ label, tabs, value, onChange }: TabsProps<T>) {
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          className="tab"
          type="button"
          role="tab"
          aria-selected={tab.id === value}
          onClick={() => {
            onChange(tab.id);
          }}
        >
          <span>{tab.label}</span>
          {tab.count !== undefined && <small>{tab.count}</small>}
        </button>
      ))}
    </div>
  );
}

interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
  id?: string;
}

/** `button.switch[role=switch]`. */
export function Switch({ checked, onChange, label, hint, disabled = false, id }: SwitchProps) {
  return (
    <button
      className="switch"
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => {
        onChange(!checked);
      }}
    >
      <span className="switch__track" aria-hidden="true" />
      <span className="switch__text">
        {label}
        {hint !== undefined && <small>{hint}</small>}
      </span>
    </button>
  );
}

/** `button.chip` with a remove icon. */
export function Chip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <button
      className="chip"
      type="button"
      aria-label={`Remove filter: ${label}`}
      onClick={onRemove}
    >
      {label}
      <Icon name="close" size="xs" />
    </button>
  );
}

/** `span.meter` with a data-driven width and colour. */
export function Meter({
  value,
  color,
  variant,
}: {
  value: number;
  color?: string;
  variant?: 'thin' | 'block';
}) {
  const width = `${Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0)) * 100}%`;
  return (
    <span className={variant ? `meter meter--${variant}` : 'meter'}>
      <span style={cssVars(color ? { w: width, c: color } : { w: width })} />
    </span>
  );
}

/** `p.loading` with a spinner. */
export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <p className="loading" role="status">
      <span className="spinner" aria-hidden="true" />
      {label}
    </p>
  );
}

/** `p.empty`. */
export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}
